import { useOs } from '@mantine/hooks';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, Card, CardContent, Grid, IconButton, Modal, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const style = {
    bgcolor: 'background.paper',
    boxShadow: 24,
    height: { lg: 550, xs: '100%' },
    left: '50%',
    maxWidth: 1250,
    position: 'absolute',
    px: 4,
    py: 2,
    top: '50%',
    transform: 'translate(-50%, -50%)',
    width: '100%',
};

interface ModalProps {
    onClose: () => void;
    open: boolean;
}

interface Platform {
    category: 'Computer' | 'Mobile';
    description: string;
    name: string;
    svg: React.ReactNode;
}

const platforms: Platform[] = [
    { category: 'Computer', description: 'Instructions for Windows PCs', name: 'Windows PC', svg: <WindowsSVG /> },
    { category: 'Computer', description: 'Instructions for Mac computers', name: 'Mac', svg: <MacSVG /> },
    { category: 'Computer', description: 'Instructions for Linux computers', name: 'Linux', svg: <LinuxSVG /> },
    {
        category: 'Computer',
        description: 'Instructions for Windows applications',
        name: 'Windows Application',
        svg: <WindowsAppSVG />,
    },
    { category: 'Mobile', description: 'Instructions for Android phones', name: 'Android Phone', svg: <AndroidSVG /> },
    { category: 'Mobile', description: 'Instructions for iOS phones', name: 'iOS Phone', svg: <IosSVG /> },
    {
        category: 'Mobile',
        description: 'Instructions for Android applications',
        name: 'Android Application',
        svg: <AndroidAppSVG />,
    },
    {
        category: 'Mobile',
        description: 'Instructions for iOS applications',
        name: 'iOS Application',
        svg: <IosAppSVG />,
    },
];

export default function WiFiModal({ onClose, open }: Readonly<ModalProps>): React.JSX.Element {
    const [view, setView] = useState<'main' | 'sub' | 'guideline'>('main');
    const [selectedCategory, setSelectedCategory] = useState<'Computer' | 'Mobile' | null>(null);
    const [selectedPlatform, setSelectedPlatform] = useState<string | null>(null);
    const os = useOs();

    useEffect(() => {
        switch (os) {
            case 'windows':
                setSelectedPlatform('Windows PC');
                break;
            case 'macos':
                setSelectedPlatform('Mac');
                break;
            case 'linux':
                setSelectedPlatform('Linux');
                break;
            case 'android':
                setSelectedPlatform('Android Phone');
                break;
            case 'ios':
                setSelectedPlatform('iOS Phone');
                break;
            default:
                setSelectedPlatform(null);
                break;
        }
    }, [os]);

    useEffect(() => {
        if (view === 'main') {
            setSelectedCategory(null);
            setSelectedPlatform(null);
        }
    }, [view]);

    const handleCategorySelect = (category: 'Computer' | 'Mobile') => {
        setSelectedCategory(category);
        setView('sub');
    };

    const handlePlatformSelect = (platform: string) => {
        setSelectedPlatform(platform);
        setView('guideline');
    };

    const renderInstructions = (platform: string) => {
        switch (platform) {
            case 'Windows PC':
                return (
                    <>
                        <Typography variant="h6">1. Open the Start Menu</Typography>
                        <Typography>
                            Click on the Start menu button located at the bottom left corner of your screen.
                        </Typography>
                        <Typography variant="h6">2. Select Settings</Typography>
                        <Typography>Click on the settings icon to open the Settings window.</Typography>
                        <Typography variant="h6">3. Go to Network & Internet</Typography>
                        <Typography>Select "Network & Internet" from the list of options.</Typography>
                        <Typography variant="h6">4. Click on Wi-Fi</Typography>
                        <Typography>Click on "Wi-Fi" to see the list of available networks.</Typography>
                    </>
                );
            case 'Mac':
                return (
                    <>
                        <Typography variant="h6">1. Open System Preferences</Typography>
                        <Typography>Click on the Apple menu and select "System Preferences".</Typography>
                        <Typography variant="h6">2. Select Network</Typography>
                        <Typography>Click on the "Network" icon to open the Network settings.</Typography>
                        <Typography variant="h6">3. Select Wi-Fi</Typography>
                        <Typography>Select "Wi-Fi" from the list on the left.</Typography>
                        <Typography variant="h6">4. View Available Networks</Typography>
                        <Typography>You will see a list of available Wi-Fi networks.</Typography>
                    </>
                );
            // Add cases for other platforms...
            default:
                return <Typography>Select a platform to see the instructions.</Typography>;
        }
    };

    const renderDownloadSection = (platform: string) => {
        switch (platform) {
            case 'Windows PC':
                return (
                    <Box>
                        <Link target="_blank" to="https://example.com/windows-app">
                            Download Windows App
                        </Link>
                    </Box>
                );
            case 'Mac':
                return (
                    <Box>
                        <Link target="_blank" to="https://example.com/mac-app">
                            Download Mac App
                        </Link>
                    </Box>
                );
            default:
                return <Typography>Select a platform to see the download link or QR code.</Typography>;
        }
    };

    return (
        <Modal onClose={onClose} open={open}>
            <Box sx={style}>
                {view === 'main' && (
                    <Box sx={{ height: '100%' }}>
                        <Typography gutterBottom variant="h4">
                            Select your device type:
                        </Typography>
                        <Grid
                            container
                            spacing={4}
                            sx={{
                                height: 'calc(100% - 50px)',
                                py: 2,
                            }}
                        >
                            <Grid size={{ sm: 6, xs: 12 }}>
                                <Card
                                    onClick={() => handleCategorySelect('Computer')}
                                    sx={{
                                        border: '1px solid #ddd',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        height: '100%',
                                        maxHeight: 420,
                                    }}
                                >
                                    <CardContent sx={{ display: 'flex', flex: 1, flexDirection: 'column' }}>
                                        <Stack
                                            sx={{
                                                flex: 1,
                                            }}
                                        >
                                            <ComputerSVG />
                                        </Stack>
                                        <Typography variant="h6">Computer</Typography>
                                        <Typography>Includes Windows, Mac, and Linux</Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid size={{ sm: 6, xs: 12 }}>
                                <Card
                                    onClick={() => handleCategorySelect('Mobile')}
                                    sx={{
                                        border: '1px solid #ddd',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        height: '100%',
                                        maxHeight: 420,
                                    }}
                                >
                                    <CardContent sx={{ display: 'flex', flex: 1, flexDirection: 'column' }}>
                                        <Stack
                                            sx={{
                                                flex: 1,
                                            }}
                                        >
                                            <MobileSVG />
                                        </Stack>
                                        <Typography variant="h6">Mobile</Typography>
                                        <Typography>Includes Android and iOS</Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </Box>
                )}

                {view === 'sub' && selectedCategory ? (
                    <Box>
                        <IconButton onClick={() => setView('main')}>
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography gutterBottom variant="h4">
                            Select your platform:
                        </Typography>
                        <Grid container spacing={2}>
                            {platforms
                                .filter((platform) => platform.category === selectedCategory)
                                .map((platform) => (
                                    <Grid key={platform.name} size={{ sm: 6, xs: 12 }}>
                                        <Card
                                            onClick={() => handlePlatformSelect(platform.name)}
                                            sx={{ border: '1px solid #ddd', cursor: 'pointer' }}
                                        >
                                            <CardContent>
                                                {platform.svg}
                                                <Typography variant="h6">{platform.name}</Typography>
                                                <Typography>{platform.description}</Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                        </Grid>
                    </Box>
                ) : null}

                {view === 'guideline' && selectedPlatform ? (
                    <Box>
                        <IconButton onClick={() => setView('sub')}>
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography gutterBottom variant="h4">
                            Instructions:
                        </Typography>
                        {renderInstructions(selectedPlatform)}
                        <Typography gutterBottom sx={{ marginTop: 4 }} variant="h4">
                            Download Link/QR Code:
                        </Typography>
                        {renderDownloadSection(selectedPlatform)}
                    </Box>
                ) : null}
            </Box>
        </Modal>
    );
}

// Example SVG images
function ComputerSVG() {
    return (
        <svg height="200" viewBox="0 0 100 100" width="200">
            <rect fill="chocolate" height="200" width="200" />
        </svg>
    );
}

function MobileSVG() {
    return (
        <svg height="200" viewBox="0 0 100 100" width="200">
            <rect fill="salmon" height="200" width="200" />
        </svg>
    );
}

function WindowsSVG() {
    return (
        <svg height="100" viewBox="0 0 100 100" width="100">
            <rect fill="blue" height="100" width="100" />
        </svg>
    );
}

function MacSVG() {
    return (
        <svg height="100" viewBox="0 0 100 100" width="100">
            <rect fill="gray" height="100" width="100" />
        </svg>
    );
}

function LinuxSVG() {
    return (
        <svg height="100" viewBox="0 0 100 100" width="100">
            <rect fill="black" height="100" width="100" />
        </svg>
    );
}

function AndroidSVG() {
    return (
        <svg height="100" viewBox="0 0 100 100" width="100">
            <rect fill="green" height="100" width="100" />
        </svg>
    );
}

function IosSVG() {
    return (
        <svg height="100" viewBox="0 0 100 100" width="100">
            <rect fill="silver" height="100" width="100" />
        </svg>
    );
}

function WindowsAppSVG() {
    return (
        <svg height="100" viewBox="0 0 100 100" width="100">
            <rect fill="lightblue" height="100" width="100" />
        </svg>
    );
}

function AndroidAppSVG() {
    return (
        <svg height="100" viewBox="0 0 100 100" width="100">
            <rect fill="lightgreen" height="100" width="100" />
        </svg>
    );
}

function IosAppSVG() {
    return (
        <svg height="100" viewBox="0 0 100 100" width="100">
            <rect fill="lightgray" height="100" width="100" />
        </svg>
    );
}
